import React, { useState } from 'react';
import AdminLayout from '../AdminLayout';
import BroadcastEditor from './components/BroadcastEditor';
import ConfirmBroadcastModal from './components/ConfirmBroadcastModal';
import { useGetRecipientCountQuery, useSendBroadcastMutation } from 'store/services/broadcast/broadcastApi';
import type { BroadcastSegment } from 'types/interfaces/broadcast';
import MagicButton from 'components/MagicButton/MagicButton';
import { toast } from 'react-toastify';
import './BroadcastPage.css';

const SEGMENT_OPTIONS: { value: BroadcastSegment; label: string }[] = [
  { value: 'all', label: 'All Users' },
  { value: 'elite', label: 'Elite Users' },
  { value: 'free', label: 'Free Users' },
];

/** Add inline styles to <img> tags for email client compatibility */
const prepareHtmlForEmail = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => {
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.display = 'block';
  });
  return doc.body.innerHTML;
};

const BroadcastPage = () => {
  const [subject, setSubject] = useState('');
  const [segment, setSegment] = useState<BroadcastSegment>('all');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyJson, setBodyJson] = useState<any>(null);
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [psContent, setPsContent] = useState('');
  const [showEmailOptions, setShowEmailOptions] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data: recipientData, isLoading: isLoadingCount } = useGetRecipientCountQuery(segment);
  const [sendBroadcast, { isLoading: isSending }] = useSendBroadcastMutation();

  const isBodyEmpty = !bodyHtml || bodyHtml === '<p></p>';
  const canSend = subject.trim().length > 0 && !isBodyEmpty;

  const handleEditorChange = (json: any, html: string) => {
    setBodyJson(json);
    setBodyHtml(html);
  };

  const handleSend = () => {
    if (!canSend) return;
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    try {
      const result = await sendBroadcast({
        subject: subject.trim(),
        body: prepareHtmlForEmail(bodyHtml),
        segment,
        ...(ctaUrl && { ctaUrl }),
        ...(ctaUrl && ctaText && { ctaText }),
        ...(psContent && { psContent }),
      }).unwrap();

      setShowConfirmModal(false);
      toast.success(
        `Broadcast sent to ${result.recipientCount.toLocaleString()} users (${result.emailCount.toLocaleString()} emails)`
      );

      // Reset form
      setSubject('');
      setBodyHtml('');
      setBodyJson({ type: 'doc', content: [{ type: 'paragraph' }] });
      setCtaUrl('');
      setCtaText('');
      setPsContent('');
      setShowEmailOptions(false);
    } catch (error: any) {
      setShowConfirmModal(false);
      const message = error?.data?.message || 'Failed to send broadcast. Please try again.';
      toast.error(message);
    }
  };

  return (
    <AdminLayout title="Broadcast Message" currentPath="/admin/message">
      <div className="broadcast-page">
        <div className="broadcast-form">
          {/* Subject */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-subject">
              Subject
            </label>
            <input
              id="broadcast-subject"
              type="text"
              className="broadcast-input"
              placeholder="Enter message subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
            <span className="broadcast-char-count">{subject.length}/200</span>
          </div>

          {/* Segment */}
          <div className="broadcast-field">
            <label className="broadcast-label" htmlFor="broadcast-segment">
              Audience
            </label>
            <select
              id="broadcast-segment"
              className="broadcast-select"
              value={segment}
              onChange={(e) => setSegment(e.target.value as BroadcastSegment)}
            >
              {SEGMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Recipient Count */}
          <div className="broadcast-recipient-count">
            {isLoadingCount ? (
              <span className="recipient-count-loading">Loading recipient count...</span>
            ) : recipientData ? (
              <span className="recipient-count-value">
                <strong>{recipientData.count.toLocaleString()}</strong> users will receive this message
                {' '}({recipientData.emailCount.toLocaleString()} will also receive an email)
              </span>
            ) : null}
          </div>

          {/* Body Editor */}
          <div className="broadcast-field">
            <label className="broadcast-label">Message Body</label>
            <BroadcastEditor content={bodyJson} onChange={handleEditorChange} />
          </div>

          {/* Email Options (collapsible) */}
          <div className="broadcast-email-options">
            <button
              className="broadcast-email-options-toggle"
              onClick={() => setShowEmailOptions(!showEmailOptions)}
            >
              {showEmailOptions ? '▾' : '▸'} Email Options (optional)
            </button>

            {showEmailOptions && (
              <div className="broadcast-email-options-content">
                <div className="broadcast-field">
                  <label className="broadcast-label" htmlFor="broadcast-cta-url">
                    CTA Button URL
                  </label>
                  <input
                    id="broadcast-cta-url"
                    type="url"
                    className="broadcast-input"
                    placeholder="https://trademagic.gg/..."
                    value={ctaUrl}
                    onChange={(e) => setCtaUrl(e.target.value)}
                  />
                </div>

                {ctaUrl && (
                  <div className="broadcast-field">
                    <label className="broadcast-label" htmlFor="broadcast-cta-text">
                      CTA Button Text
                    </label>
                    <input
                      id="broadcast-cta-text"
                      type="text"
                      className="broadcast-input"
                      placeholder="Check It Out"
                      value={ctaText}
                      onChange={(e) => setCtaText(e.target.value)}
                    />
                  </div>
                )}

                <div className="broadcast-field">
                  <label className="broadcast-label" htmlFor="broadcast-ps">
                    P.S. Content
                  </label>
                  <input
                    id="broadcast-ps"
                    type="text"
                    className="broadcast-input"
                    placeholder="P.S. Don't forget to..."
                    value={psContent}
                    onChange={(e) => setPsContent(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Send Button */}
          <div className="broadcast-actions">
            <MagicButton
              label="Send Broadcast"
              color="purple"
              onPress={handleSend}
              isDisabled={!canSend}
              fullWidth={false}
            />
          </div>
        </div>
      </div>

      <ConfirmBroadcastModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSend}
        isLoading={isSending}
        recipientCount={recipientData?.count ?? 0}
        emailCount={recipientData?.emailCount ?? 0}
        segment={segment}
      />
    </AdminLayout>
  );
};

export default BroadcastPage;
